import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * A registry that knows how to return modules by path (import path).
 */
export class ModuleRegistry {
  constructor() {
    _defineProperty(this, "moduleImports", {});
    /**
     * Modules exposed as a plain old javascript object.
     * Keys are import paths, values are promises to the module.
     * (Getter returns a promise while setter takes just the module (not a promise to the module)).
     */
    _defineProperty(this, "modules", void 0);
    const self = this;
    const retrieveModule = this.retrieveModule.bind(this);
    const setModule = this.setModule.bind(this);
    const moduleProxy = new Proxy(this.moduleImports, {
      get(target, prop) {
        if (target !== self.moduleImports) {
          throw new Error("Unknown proxy target");
        }
        return retrieveModule(prop);
      },
      set(target, prop, value) {
        if (target !== self.moduleImports) {
          throw new Error("Unknown proxy target");
        }
        setModule(prop, value);
        return true;
      }
    });
    // the proxy object has values that are promises to module, not function that returns promise to module
    this.modules = moduleProxy;
  }

  /**
   * Retrieve a module by its path, handling the case where the module isn't found.
   */
  retrieveModule(importPath) {
    const importer = this.moduleImports[importPath];
    if (importer == null) {
      return this.onMissingModule(importPath);
    }
    // check existence
    return importer();
  }

  /**
   * Retrieve a module by its path, or return undefined if module is unknown.
   * Used in the case where module is expected to be undefined.
   */
  retrieveModuleIfDefined(importPath) {
    const importer = this.moduleImports[importPath];
    return importer != null ? importer() : undefined;
  }
  setModule(importPath, value) {
    const importer = async () => value;
    if (importPath in this.moduleImports) {
      throw new Error(`Attempt to set already defined module: ${importPath}`);
    }
    this.moduleImports[importPath] = importer;
  }

  /**
   * If an unknown module is referenced, possibly take some other action to retrieve it.  Or throw.
   * @param prop module import path
   * @return a promise to the module, if loaded (otherwise an exception is thrown).
   */
  onMissingModule(prop) {
    throw new Error(`Attempt to retrieve unknown module: ${prop}`);
  }

  /**
   * Add modules to the registry.
   * @param modules An object where keys are import strings and values are async functions that
   *  return a promise to the module.
   */
  registerModules(newModules) {
    // define additional properties on the module registry
    for (const [path, importFunction] of Object.entries(newModules)) {
      if (path in this.moduleImports) {
        throw new Error(`Attempt to register duplicate module ${path}`);
      }
      this.moduleImports[path] = importFunction;
    }
  }

  /**
   * Return the list of paths of all modules currently registered.
   */
  get registeredModulePaths() {
    return Object.keys(this.moduleImports);
  }
}

// the registry is meant to be a singleton for the whole application.
// it's allowed to temporarily be overwritten for testing purposes only.
export let abModuleRegistry = new ModuleRegistry();
// plugins will use window.abModuleRegistry, so export the variable
window.abModuleRegistry = abModuleRegistry;
// window.abModuleRegistry.modules["<module path"] should return a Promise to the module.
// window.abModuleRegistry.registerModules(...) should register additional modules.

// helper to overwrite the module registry temporarily for testing.
export function setModuleRegistryForTest(moduleRegistry) {
  abModuleRegistry = moduleRegistry;
}
//# sourceMappingURL=ModuleRegistry.js.map
